import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { API_ERRORS, getApiErrorName } from '../../../services/api';
import { useToast } from '../../../hooks/toast';
import { useRoundCache } from '../../../hooks/useRoundCache';
import { RoboPintorForm } from './RoboPintorForm';
import { SeguidorLinhaForm } from './SeguidorLinhaForm';
import { ScorecardView, IScorecard } from './ScorecardView';
import {
  AlertBadge,
  Container,
  HeaderContent,
  LoadingWrapper,
  MainCard,
  ScorecardNavRow,
  StopwatchContainer,
  StopwatchDisplay,
} from './styles';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import SecondaryButton from '../../../components/Buttons/SecondaryButton';
import { regulationFor } from '../../../utils/tournament/modalityRules';

interface IModalityRef {
  evaluation_type: 'binary' | 'subjective';
  bonus_time_seconds: number;
  bonus_points: number;
  penalty_per_occurrence: number;
  max_score?: number;
  has_technical_presentation?: boolean;
}

const NOT_A_JUDGE_MESSAGE =
  'Você não está cadastrado como juiz deste torneio. Peça à organização para incluir você na lista de juízes — o que já foi preenchido continua salvo neste navegador.';

/**
 * Sem catálogo vinculado, usa o regulamento DA MODALIDADE da rodada — antes
 * era um padrão fixo (o do Seguidor de Linha) inclusive para o Robô Pintor.
 */
const regulationModalityRef = (slug: string): IModalityRef => {
  const rules = regulationFor(slug);
  return {
    evaluation_type: rules.evaluation_type,
    bonus_time_seconds: rules.bonus_time_seconds,
    bonus_points: rules.bonus_points,
    penalty_per_occurrence: rules.penalty_per_occurrence,
  };
};

interface ITournamentModality {
  id: string;
  modality: string;
  max_rounds?: number;
  time_limit_seconds?: number;
  time_periods_seconds?: number;
  bonus_time_seconds?: number;
  bonus_points?: number;
  modality_ref: IModalityRef | null;
}

interface IRoundEvaluation {
  id: string;
  slot_id: string;
  round_number: number;
  status: 'pending' | 'in_progress' | 'scored';
  team: {
    id: string;
    name: string;
  };
  tournament_modality: ITournamentModality;
}

interface IEvaluationSubmitData {
  mission_scores: { mission_order: number; score: number }[];
  touch_count?: number;
  infraction_count?: number;
}

type AlertLevel = 'normal' | 'warning' | 'critical' | 'ended';

interface IRoundListItem {
  id: string;
  slot_id: string;
  round_number: number;
  status: string;
}

interface IAxiosError {
  response?: { status?: number; data?: { message?: string } };
}

const MODALITY_LABELS: Record<string, string> = {
  robo_pintor: 'Robo Pintor',
  seguidor_de_linha: 'Seguidor de linha',
};

// Schedules a sine-wave beep using the Web Audio API.
function scheduleBeep(
  ctx: AudioContext,
  frequency: number,
  startOffset: number,
  duration: number,
  volume = 0.6,
) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.frequency.value = frequency;
  osc.type = 'sine';
  const t = ctx.currentTime + startOffset;
  gain.gain.setValueAtTime(volume, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + duration);
  osc.start(t);
  osc.stop(t + duration);
}

const TournamentRoundEvaluation: React.FC = () => {
  // round_id in the URL is actually the slot_id (navigation from TournamentDetails)
  const { id, round_id: slotId } = useParams<{
    id: string;
    round_id: string;
  }>();
  const [searchParams] = useSearchParams();
  const roundNumber = parseInt(searchParams.get('round') ?? '1', 10);

  const navigate = useNavigate();
  const { addToast } = useToast();

  const isViewOnly = searchParams.get('view') === 'result';

  const [isLoading, setIsLoading] = useState(true);
  const [round, setRound] = useState<IRoundEvaluation | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scorecard, setScorecard] = useState<IScorecard | null>(null);
  const [isScorecardLoading, setIsScorecardLoading] = useState(false);
  const [scorecardError, setScorecardError] = useState(false);

  // Cache key includes round number so each round has independent timer + form state
  const cacheKey = `round_evaluate_${slotId}_r${roundNumber}`;

  const [timerState, setTimerState] = useRoundCache(`${cacheKey}_timer`, {
    seconds: 0,
    isRunning: false,
  });

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Persisted AudioContext — must be created/resumed on a user gesture (iOS requirement)
  const audioCtxRef = useRef<AudioContext | null>(null);
  // Tracks which one-shot alerts have already fired for the current run
  const alertTriggeredRef = useRef(new Set<string>());

  const getAudioCtx = useCallback((): AudioContext | null => {
    if (!audioCtxRef.current) {
      const w = window as Window &
        typeof globalThis & { webkitAudioContext?: typeof AudioContext };
      const Ctor = window.AudioContext ?? w.webkitAudioContext;
      if (Ctor) audioCtxRef.current = new Ctor();
    }
    return audioCtxRef.current;
  }, []);

  const fetchExistingRound = useCallback(async () => {
    const { data: list } = await api.get<IRoundListItem[]>(
      `/tournaments/${id}/rounds`,
    );
    const existing = list.find(
      r => r.slot_id === slotId && r.round_number === roundNumber,
    );
    if (existing) {
      const { data: detail } = await api.get<IRoundEvaluation>(
        `/tournaments/${id}/rounds/${existing.id}`,
      );
      setRound(detail);
    } else {
      addToast({
        type: 'error',
        expression: 'Erro',
        description: 'Avaliação não encontrada.',
      });
      navigate(`/tournament/${id}`);
    }
  }, [id, slotId, roundNumber, addToast, navigate]);

  const openOrFetchRound = useCallback(async () => {
    if (!id || !slotId) return;
    setIsLoading(true);
    try {
      if (isViewOnly) {
        await fetchExistingRound();
        return;
      }
      const { data } = await api.post<IRoundEvaluation>(
        `/tournaments/${id}/rounds`,
        { slot_id: slotId },
      );
      setRound(data);
    } catch (err: unknown) {
      const axiosErr = err as IAxiosError;
      const status = axiosErr?.response?.status;
      if (getApiErrorName(err) === API_ERRORS.ROUND_ALREADY_EVALUATED) {
        // Slot gêmeo de um cronograma duplicado: a rodada já tem avaliação.
        addToast({
          type: 'error',
          expression: 'Rodada já avaliada',
          description:
            axiosErr?.response?.data?.message ??
            'Esta equipe já tem uma avaliação desta rodada.',
        });
        navigate(`/tournament/${id}`);
      } else if (status === 409) {
        try {
          await fetchExistingRound();
        } catch {
          addToast({
            type: 'error',
            expression: 'Erro',
            description: 'Falha ao carregar avaliação existente.',
          });
          navigate(`/tournament/${id}`);
        }
      } else if (getApiErrorName(err) === API_ERRORS.NOT_A_JUDGE) {
        addToast({
          type: 'error',
          expression: 'Só juízes do torneio avaliam',
          description: NOT_A_JUDGE_MESSAGE,
        });
        navigate(`/tournament/${id}`);
      } else {
        const message =
          axiosErr?.response?.data?.message ?? 'Falha ao abrir avaliação.';
        addToast({ type: 'error', expression: 'Erro', description: message });
        navigate(`/tournament/${id}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    id,
    slotId,
    roundNumber,
    isViewOnly,
    fetchExistingRound,
    addToast,
    navigate,
  ]);

  useEffect(() => {
    openOrFetchRound();
  }, [openOrFetchRound]);

  useEffect(() => {
    if (!id || !round || round.status !== 'scored' || scorecardError) return;
    setIsScorecardLoading(true);
    api
      .get<IScorecard>(`/tournaments/${id}/rounds/${round.id}/scorecard`)
      .then(({ data }) => setScorecard(data))
      .catch(() => {
        addToast({
          type: 'error',
          expression: 'Erro',
          description: 'Não foi possível carregar o scorecard.',
        });
        setScorecardError(true);
      })
      .finally(() => setIsScorecardLoading(false));
  }, [id, round?.id, round?.status, scorecardError, addToast]);

  // Main countdown tick
  useEffect(() => {
    if (timerState.isRunning) {
      timerRef.current = setInterval(() => {
        setTimerState(prev => ({ ...prev, seconds: prev.seconds + 1 }));
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timerState.isRunning, setTimerState]);

  // Reset one-shot alert flags when timer returns to 0 (after reset)
  useEffect(() => {
    if (timerState.seconds === 0) {
      alertTriggeredRef.current.clear();
    }
  }, [timerState.seconds]);

  // Sound + vibration feedback at key time thresholds
  useEffect(() => {
    if (!timerState.isRunning || !round) return;

    const modality = round.tournament_modality;
    const modRef =
      modality.modality_ref ?? regulationModalityRef(modality.modality);
    const bonusTime = modality.bonus_time_seconds ?? modRef.bonus_time_seconds;
    const timeLeft = bonusTime - timerState.seconds;

    const once = (key: string, fn: () => void) => {
      if (!alertTriggeredRef.current.has(key)) {
        alertTriggeredRef.current.add(key);
        fn();
      }
    };

    // 30s warning: single medium beep + short vibration
    if (timeLeft === 30) {
      once('warn30', () => {
        const ctx = getAudioCtx();
        if (ctx) scheduleBeep(ctx, 440, 0, 0.35, 0.5);
        navigator.vibrate?.(250);
      });
    }

    // 10s warning: two quick beeps + double pulse vibration
    if (timeLeft === 10) {
      once('warn10', () => {
        const ctx = getAudioCtx();
        if (ctx) {
          scheduleBeep(ctx, 880, 0, 0.2, 0.65);
          scheduleBeep(ctx, 880, 0.28, 0.2, 0.65);
        }
        navigator.vibrate?.([150, 80, 150]);
      });
    }

    // Final 5-second countdown: sharp beep + quick vibration each second
    if (timeLeft > 0 && timeLeft <= 5) {
      once(`count${timeLeft}`, () => {
        const ctx = getAudioCtx();
        if (ctx) scheduleBeep(ctx, 1100, 0, 0.1, 0.75);
        navigator.vibrate?.(80);
      });
    }

    // Time's up: ascending triad + long vibration pattern
    if (timeLeft === 0) {
      once('end', () => {
        const ctx = getAudioCtx();
        if (ctx) {
          scheduleBeep(ctx, 523, 0, 0.25, 0.85); // C5
          scheduleBeep(ctx, 659, 0.3, 0.25, 0.85); // E5
          scheduleBeep(ctx, 784, 0.6, 0.55, 0.9); // G5
        }
        navigator.vibrate?.([300, 100, 300, 100, 500]);
      });
    }
  }, [timerState.seconds, timerState.isRunning, round, getAudioCtx]);

  const toggleTimer = () => {
    if (!timerState.isRunning) {
      // Resume AudioContext on user gesture — required by iOS/Chrome autoplay policy
      getAudioCtx()?.resume();
    }
    setTimerState(prev => ({ ...prev, isRunning: !prev.isRunning }));
  };

  const formatTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const handleSubmitEvaluation = async (
    evaluationData: IEvaluationSubmitData,
  ) => {
    if (!id || !round) return;
    setIsSubmitting(true);
    try {
      setTimerState(prev => ({ ...prev, isRunning: false }));

      await api.patch(`/tournaments/${id}/rounds/${round.id}`, {
        mission_scores: evaluationData.mission_scores,
        completion_time_seconds:
          timerState.seconds > 0 ? timerState.seconds : null,
        touch_count: evaluationData.touch_count ?? 0,
        infraction_count: evaluationData.infraction_count ?? 0,
      });

      addToast({
        type: 'success',
        expression: 'Sucesso',
        description: `Rodada ${roundNumber} registrada com sucesso.`,
      });

      window.localStorage.removeItem(`${cacheKey}_timer`);
      window.localStorage.removeItem(`round_evaluate_${cacheKey}_robopintor`);
      window.localStorage.removeItem(
        `round_evaluate_${cacheKey}_seguidorlinha`,
      );

      // Navigate immediately to tournament details
      navigate(`/tournament/${id}`);
    } catch (err: unknown) {
      if (getApiErrorName(err) === API_ERRORS.NOT_A_JUDGE) {
        // O rascunho fica salvo no navegador: um juiz do torneio pode retomar.
        addToast({
          type: 'error',
          expression: 'Só juízes do torneio avaliam',
          description: NOT_A_JUDGE_MESSAGE,
        });
        navigate(`/tournament/${id}`);
        return;
      }
      const axiosErr = err as IAxiosError;
      const message =
        axiosErr?.response?.data?.message ??
        'Não foi possível registrar a avaliação.';
      addToast({ type: 'error', expression: 'Erro', description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingWrapper>
          <p>Carregando...</p>
        </LoadingWrapper>
      </Container>
    );
  }

  if (!round) return null;

  const maxRounds = round.tournament_modality.max_rounds ?? 1;

  if (round.status === 'scored') {
    if (scorecardError) {
      return (
        <Container>
          <HeaderContent>
            <h1>Erro ao carregar resultado</h1>
          </HeaderContent>
          <MainCard>
            <p style={{ color: '#555', marginBottom: '1rem' }}>
              Não foi possível carregar o resultado desta rodada.
            </p>
            <ScorecardNavRow>
              <SecondaryButton
                type="button"
                onClick={() => navigate(`/tournament/${id}`)}
              >
                Voltar ao torneio
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={() => setScorecardError(false)}
              >
                Tentar novamente
              </PrimaryButton>
            </ScorecardNavRow>
          </MainCard>
        </Container>
      );
    }
    if (isScorecardLoading || !scorecard) {
      return (
        <Container>
          <LoadingWrapper>
            <p>Carregando resultado...</p>
          </LoadingWrapper>
        </Container>
      );
    }
    return (
      <ScorecardView
        scorecard={scorecard}
        roundNumber={roundNumber}
        maxRounds={maxRounds}
        tournamentId={id ?? ''}
      />
    );
  }

  const { modality } = round.tournament_modality;
  const modality_ref =
    round.tournament_modality.modality_ref ??
    regulationModalityRef(round.tournament_modality.modality);
  const modalityLabel = MODALITY_LABELS[modality] ?? modality;

  // Use tournament-specific bonus_time_seconds; fall back to modality catalog default
  const bonusTime =
    round.tournament_modality.bonus_time_seconds ??
    modality_ref.bonus_time_seconds;
  const timeLeft = bonusTime - timerState.seconds;

  // Merge tournament-configured bonus values over catalog defaults so forms use the right thresholds
  const effectiveModalityRef: IModalityRef = {
    ...modality_ref,
    bonus_time_seconds: bonusTime,
    bonus_points:
      round.tournament_modality.bonus_points ?? modality_ref.bonus_points,
  };
  const alertLevel: AlertLevel =
    timerState.seconds === 0
      ? 'normal'
      : timerState.seconds >= bonusTime
        ? 'ended'
        : timeLeft <= 10
          ? 'critical'
          : timeLeft <= 30
            ? 'warning'
            : 'normal';

  const alertMessage =
    alertLevel === 'ended'
      ? 'Tempo do bônus encerrado'
      : alertLevel === 'critical'
        ? `${timeLeft}s para o bônus!`
        : alertLevel === 'warning'
          ? `${timeLeft}s para o bônus`
          : null;

  return (
    <Container>
      <HeaderContent>
        <h1>
          Rodada {roundNumber}/{maxRounds} · {round.team.name} · {modalityLabel}
        </h1>
      </HeaderContent>

      <MainCard>
        <StopwatchContainer>
          <h3>Cronômetro da prova — {formatTime(bonusTime)} para bônus</h3>
          <StopwatchDisplay $alertLevel={alertLevel}>
            <div className="time">{formatTime(timerState.seconds)}</div>
            <PrimaryButton type="button" onClick={toggleTimer}>
              {timerState.isRunning ? 'Parar' : 'Iniciar'}
            </PrimaryButton>

            <SecondaryButton
              type="button"
              onClick={() => setTimerState({ seconds: 0, isRunning: false })}
            >
              Resetar
            </SecondaryButton>
          </StopwatchDisplay>

          {alertMessage && (
            <AlertBadge $level={alertLevel as 'warning' | 'critical' | 'ended'}>
              {alertMessage}
            </AlertBadge>
          )}
        </StopwatchContainer>

        {modality === 'robo_pintor' && (
          <RoboPintorForm
            roundId={cacheKey}
            modalityRef={effectiveModalityRef}
            timerSeconds={timerState.seconds}
            onSubmit={handleSubmitEvaluation}
            isSubmitting={isSubmitting}
          />
        )}

        {modality === 'seguidor_de_linha' && (
          <SeguidorLinhaForm
            roundId={cacheKey}
            modalityRef={effectiveModalityRef}
            timerSeconds={timerState.seconds}
            onSubmit={handleSubmitEvaluation}
            isSubmitting={isSubmitting}
          />
        )}

        {modality !== 'robo_pintor' && modality !== 'seguidor_de_linha' && (
          <p>Avaliação não implementada para a modalidade {modalityLabel}.</p>
        )}
      </MainCard>
    </Container>
  );
};

export default TournamentRoundEvaluation;
